// ---------------------------------------------------------------------------
// VerticalBarChart -- Vertical Capsule Bars (Column Chart)
// ---------------------------------------------------------------------------
// Renders a sorted set of vertical capsule bars with:
//   - Automatic sorting (descending by value)
//   - Platform icons above bars (when label matches a known platform)
//   - Staggered height animation (bars grow upward)
//   - Sequential Felton palette colors
//   - Value + percentage display below each bar
// ---------------------------------------------------------------------------

import React, { useMemo } from 'react'
import { getChartColorCss } from '../constants/chartColors'
import { CHART_ANIMATION } from '../constants/chartAnimation'
import { ChartCard } from './ChartCard'
import { getPlatformIcon } from '../../shared/platformIcons'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface VerticalBarDataItem {
  readonly label: string
  readonly value: number
}

export interface VerticalBarChartProps {
  /** Data items to render as vertical bars */
  readonly data: ReadonlyArray<VerticalBarDataItem>
  /** Card title */
  readonly title: string
  /** Optional card subtitle */
  readonly subtitle?: string
  /** Maximum number of bars to display (default 5) */
  readonly maxItems?: number
  /** Show platform icons if label matches a known platform */
  readonly showIcons?: boolean
  /** Preserve original data order instead of sorting by value (default false) */
  readonly preserveOrder?: boolean
  /** Additional CSS class on the container */
  readonly className?: string
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const DEFAULT_MAX_ITEMS = 5
const MAX_BAR_HEIGHT_PX = 120

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Compute bar height percentage (relative to max value for visual scaling) */
function computeBarHeight(value: number, maxValue: number): number {
  const safeMax = maxValue > 0 ? maxValue : 1
  const raw = (value / safeMax) * 100
  return Math.max(0, Math.min(100, raw))
}

/** Compute actual percentage (relative to total sum) */
function computePercentOfTotal(value: number, total: number): number {
  const safeTotal = total > 0 ? total : 1
  const raw = (value / safeTotal) * 100
  return Math.max(0, Math.min(100, raw))
}

function formatPercentLabel(percent: number): string {
  return `(${Math.round(percent)}%)`
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const VerticalBarChart: React.FC<VerticalBarChartProps> = ({
  data,
  title,
  subtitle,
  maxItems = DEFAULT_MAX_ITEMS,
  showIcons = true,
  preserveOrder = false,
  className,
}) => {
  // Sort descending and limit (unless preserveOrder is true)
  const sortedData = useMemo(() => {
    const items = preserveOrder
      ? [...data]
      : [...data].sort((a, b) => b.value - a.value)
    return items.slice(0, maxItems)
  }, [data, maxItems, preserveOrder])

  const maxValue = useMemo(
    () => sortedData.reduce((max, item) => Math.max(max, item.value), 0),
    [sortedData],
  )

  const totalValue = useMemo(
    () => sortedData.reduce((sum, item) => sum + item.value, 0),
    [sortedData],
  )

  // Empty state when no data
  if (sortedData.length === 0) {
    return (
      <ChartCard title={title} subtitle={subtitle} className={className}>
        <div className="flex items-center justify-center min-h-[120px] text-v11-text-muted text-sm">
          No data available for this period
        </div>
      </ChartCard>
    )
  }

  return (
    <ChartCard title={title} subtitle={subtitle} className={className}>
      <div
        data-testid="vertical-bar-container"
        className="flex items-end justify-center gap-4"
      >
        {sortedData.map((item, index) => {
          const barHeightPercent = computeBarHeight(item.value, maxValue)
          const labelPercent = computePercentOfTotal(item.value, totalValue)
          const platform = showIcons ? getPlatformIcon(item.label) : null
          const fillColor = platform ? platform.color : getChartColorCss(index)

          const barStyle: React.CSSProperties = {
            height: `${barHeightPercent}%`,
            backgroundColor: fillColor,
            transition: `height ${CHART_ANIMATION.duration}ms ${CHART_ANIMATION.easing} ${index * CHART_ANIMATION.staggerDelay}ms`,
          }

          return (
            <div
              key={`${item.label}-${index}`}
              data-testid="vertical-bar-column"
              className="flex flex-col items-center gap-2"
            >
              {/* Platform icon */}
              {platform !== null && (
                <div
                  data-testid="platform-icon-wrapper"
                  className="flex h-8 w-8 items-center justify-center rounded-full"
                  style={{ color: platform.color }}
                >
                  {platform.icon}
                </div>
              )}

              {/* Capsule bar container (fixed height, bar grows from bottom) */}
              <div
                className="flex items-end"
                style={{ height: `${MAX_BAR_HEIGHT_PX}px` }}
              >
                <div
                  data-testid="vertical-bar"
                  role="meter"
                  aria-valuenow={item.value}
                  aria-valuemin={0}
                  aria-valuemax={maxValue}
                  aria-label={`${item.label}: ${item.value}`}
                  className="w-12 rounded-full"
                  style={barStyle}
                />
              </div>

              {/* Value + percentage */}
              <div className="flex flex-col items-center">
                <span className="text-sm font-bold tabular-nums text-v11-text-primary">
                  {item.value.toLocaleString('en-US')}
                </span>
                <span className="text-xs font-medium text-v11-text-muted">
                  {formatPercentLabel(labelPercent)}
                </span>
              </div>

              {/* Label */}
              <span
                data-testid="vertical-bar-label"
                className="max-w-[72px] truncate text-center text-xs text-v11-text-secondary"
              >
                {item.label}
              </span>
            </div>
          )
        })}
      </div>
    </ChartCard>
  )
}
